import AuthTeams from "../../auth/AuthTeams";
import { cleanAll } from "../../components/checkinDocuments/ClassFormatTreeLoad";
import {
  getDocClassCodeOnly,
  sanitizeFileName,
  isWorkingDocumentDownloaded,
} from "../../functions/helperMethod";
import { parseIFSFileName } from "../../functions/ifsFileName";
import { resetHierarchy } from "../../functions/resetHierarchy";
import { resolveGroupId } from "../check-in-new-list/checkInNewList.utils";
import {
  DATA_LOAD_STATUS,
  DOCUMENT_LOAD_STATUS,
} from "../../redux/constants/metaData";

const WORD_VIEW_COPY_EXTENSIONS = new Set(["DOC", "DOCX", "DOCM", "DOTM"]);

/** True when the original file can use auto-generated view copy (Word only). */
export function isWordLikeFileName(fileName) {
  if (typeof fileName !== "string" || fileName.trim() === "") {
    return false;
  }
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot < 0 || lastDot === fileName.length - 1) {
    return false;
  }
  const ext = fileName
    .substring(lastDot + 1)
    .trim()
    .toUpperCase();
  return WORD_VIEW_COPY_EXTENSIONS.has(ext);
}

/**
 * View copy is satisfied when not required, a PDF was uploaded, or auto-generate
 * is enabled for a Word document.
 */
export function isViewCopyRequirementMet({
  viewRequired,
  uploadViewCopyCount = 0,
  createViewCopy = false,
  fileName = "",
}) {
  const viewReq =
    viewRequired === true || viewRequired === "Y" || viewRequired === "y";
  if (!viewReq) {
    return true;
  }
  if (uploadViewCopyCount > 0) {
    return true;
  }
  return createViewCopy === true && isWordLikeFileName(fileName);
}

export function resolveDocFormat(providedFormat, fileName, formatRequired) {
  if (providedFormat != null && String(providedFormat).trim() !== "") {
    return String(providedFormat).trim();
  }
  if (formatRequired !== true && formatRequired !== "Y") {
    return "";
  }
  if (typeof fileName !== "string") {
    return "";
  }
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot < 0 || lastDot === fileName.length - 1) {
    return "";
  }
  return fileName
    .substring(lastDot + 1)
    .trim()
    .toUpperCase();
}

export const cloneViewCopyFiles = (files) =>
  Array.isArray(files) ? files.map((file) => ({ ...file })) : [];

/** First uploaded view-copy PDF on a file draft (bulk inline upload). */
export function getPrimaryViewCopyAttachment(draft) {
  const files = draft?.viewCopyFiles;
  if (!Array.isArray(files)) {
    return null;
  }
  return (
    files.find(
      (file) =>
        typeof file?.base64str === "string" && file.base64str.length > 0,
    ) ?? null
  );
}

export function draftHasManualViewCopyAttachment(draft) {
  return getPrimaryViewCopyAttachment(draft) != null;
}

const isApiFlagYes = (value) => value === true || value === "Y";

/** View-required flag for a file draft (inherits from Settings when not overridden). */
export function resolveBulkViewRequiredFlag(draft, settingsDraft) {
  if (isApiFlagYes(draft?.viewRequired)) {
    return true;
  }
  if (!isApiFlagYes(settingsDraft?.viewRequired)) {
    return false;
  }
  if (!draft?.overridden) {
    return true;
  }
  const draftClass = String(draft?.docClass ?? "").trim();
  const settingsClass = String(settingsDraft?.docClass ?? "").trim();
  const draftFormat = String(draft?.docFormat ?? "").trim();
  const settingsFormat = String(settingsDraft?.docFormat ?? "").trim();
  return (
    draftClass !== "" &&
    draftClass === settingsClass &&
    draftFormat === settingsFormat
  );
}

/** Upload count from draft fields (count + attached PDF list). */
export function getEffectiveViewCopyUploadCount(draft) {
  const count = draft?.uploadViewCopyCount ?? 0;
  if (count > 0) {
    return count;
  }
  return draftHasManualViewCopyAttachment(draft) ? 1 : 0;
}

export function mergeFileViewCopyIntoDraft(draft, viewCopyFiles, uploadViewCopyCount) {
  const files = cloneViewCopyFiles(viewCopyFiles);
  const count = uploadViewCopyCount ?? 0;
  const hasAttachment = files.some(
    (file) => typeof file?.base64str === "string" && file.base64str.length > 0,
  );
  return {
    ...draft,
    viewCopyFiles: files,
    uploadViewCopyCount: count,
    viewCopyOverridden:
      count > 0 || hasAttachment ? true : draft?.viewCopyOverridden,
  };
}

export function mergeActiveFileViewCopyIntoDraftMap(
  draftByKey,
  activeKey,
  viewCopyFiles,
  uploadViewCopyCount,
) {
  if (!activeKey?.startsWith("file:")) {
    return draftByKey;
  }
  const fileKey = activeKey.slice(5);
  const row = draftByKey[fileKey] ?? {};
  const merged = mergeFileViewCopyIntoDraft(
    row,
    viewCopyFiles,
    uploadViewCopyCount,
  );
  if (
    merged.viewCopyFiles === row.viewCopyFiles &&
    merged.uploadViewCopyCount === row.uploadViewCopyCount
  ) {
    return draftByKey;
  }
  return { ...draftByKey, [fileKey]: merged };
}

export function mapObjectConnections(selectedObjects) {
  const selectedList = Array.isArray(selectedObjects) ? selectedObjects : [];
  return selectedList.map((item) => ({
    luName: item.luName,
    keyRef: item.keyRef,
    updateRevision: "",
    updateRevisionCode: "",
    KeepLastDocRev: "LatestRevision",
    SurveyLockedFlag: "Unlocked",
    SurveyLockedFlagDb: "0",
    KeepLastDocRevDb: "L",
  }));
}

/** Use list-provided download URL when present (no extra API call). */
export function getBulkOneDriveFileUrlFromDoc(doc) {
  if (typeof doc?.downloadlink !== "string" || doc.downloadlink.trim() === "") {
    return null;
  }
  return doc.downloadlink.trim();
}

/**
 * OneDrive context for server-side Graph URL resolution during check-in-bulk.
 * @returns {object|null}
 */
export function buildBulkOneDriveServerContext(doc, teamsContext) {
  // Local Browse files use synthetic keys — never send them as Graph item ids.
  if (doc?.source === "browse") {
    return null;
  }
  const docId = doc?.key;
  if (!docId) {
    return null;
  }
  const tenantId = teamsContext?.user?.tenant?.id ?? "";
  const userId = teamsContext?.user?.id ?? "";
  const { groupId, channelId, fileRoot } = resolveGroupId(teamsContext);
  if (!tenantId || !userId || !fileRoot) {
    return null;
  }
  return {
    oneDriveDocumentId: String(docId),
    oneDriveRoot: fileRoot,
    oneDriveTeamId: groupId ?? "",
    oneDriveChannelId: channelId ?? "",
    oneDriveUserId: userId,
    oneDriveTenantId: tenantId,
  };
}

/**
 * Build a single check-in request body (matches checkinDocumentThunk / DocInfoModel).
 */
export function buildBulkDocInfo({
  doc,
  draft,
  fileUrl,
  oneDriveServerContext,
  formatRequired,
  createViewCopy,
  viewRequired,
  uploadViewCopyCount,
  docReleaseStatus,
  selectedObjectConnections,
}) {
  const fileName = doc?.name ?? doc?.title ?? "";
  const docClassCode = getDocClassCodeOnly(draft?.docClass ?? "");
  const resolvedDocFormat = resolveDocFormat(
    draft?.docFormat,
    fileName,
    formatRequired,
  );
  const webUser = new AuthTeams().getEmail();
  const viewReq = viewRequired === true || viewRequired === "Y";
  const manualViewCopy = getPrimaryViewCopyAttachment(draft);
  const hasManualViewCopy = manualViewCopy != null;
  const effectiveCreateViewCopy =
    hasManualViewCopy ? false : createViewCopy === true;
  const status =
    docReleaseStatus === false
      ? ""
      : hasManualViewCopy || uploadViewCopyCount > 0
        ? ""
        : docReleaseStatus;

  const trimmedFileUrl =
    typeof fileUrl === "string" && fileUrl.trim() !== "" ? fileUrl.trim() : "";
  const useServerOneDrive =
    !trimmedFileUrl &&
    oneDriveServerContext?.oneDriveDocumentId != null &&
    String(oneDriveServerContext.oneDriveDocumentId).trim() !== "";

  const trimmedFileData =
    typeof doc?.base64str === "string" && doc.base64str.trim() !== ""
      ? doc.base64str.trim()
      : "";

  const browseFile =
    doc?.source === "browse" &&
    typeof Blob !== "undefined" &&
    doc?.file instanceof Blob
      ? doc.file
      : null;

  return {
    checkinViewCopy: false,
    docClass: docClassCode,
    title: draft?.docTitle ?? "",
    docFormat: resolvedDocFormat,
    fileData: browseFile ? "" : trimmedFileData,
    ...(trimmedFileUrl && !browseFile ? { fileUrl: trimmedFileUrl } : {}),
    ...(useServerOneDrive && !browseFile ? oneDriveServerContext : {}),
    fileName: sanitizeFileName(fileName),
    objectConnections: mapObjectConnections(selectedObjectConnections),
    createViewCopy: effectiveCreateViewCopy,
    rollbackRevisionOnFail: viewReq,
    webUser,
    status,
    ...(browseFile ? { __browseFile: browseFile } : {}),
    ...(hasManualViewCopy
      ? {
          viewCopyFileName: manualViewCopy.name ?? "",
          viewCopyFileData: manualViewCopy.base64str,
        }
      : {}),
  };
}

/** Toast title after bulk check-in. */
export function getBulkCheckinToastTitle(failureCount) {
  return (Number(failureCount) || 0) > 0
    ? "Bulk check-in completed with errors"
    : "Bulk check-in completed";
}

/** Toast body: e.g. "2 successful, 1 failed." */
export function formatBulkCheckinToastMessage(successCount, failureCount) {
  const succeeded = Number(successCount) || 0;
  const failed = Number(failureCount) || 0;
  const successPart =
    succeeded === 1 ? "1 Document checked in successfully" : `${succeeded} Documents checked in successfully`;
  if (failed === 0) {
    return `${successPart}.`;
  }
  const failurePart = failed === 1 ? "1 Document failed to check in" : `${failed} Documents failed to check in`;
  return `${successPart}, ${failurePart}.`;
}

/** Bulk API items with HTTP 200. */
export function getSuccessfulBulkResults(bulk) {
  return (bulk?.results ?? []).filter((item) => item.statusCode === 200);
}

/** Resolve whether the OneDrive original should be deleted for a bulk file row. */
export function resolveOneDriveDeleteRequired(draft, settingsDraft) {
  return draft?.oneDriveDeleteOverridden
    ? draft.oneDriveDeleteRequired === true
    : settingsDraft?.oneDriveDeleteRequired === true;
}

/**
 * Collect OneDrive file ids to delete after bulk check-in.
 * Results are aligned by index with the submitted `docInfos` / `documents` arrays.
 */
export function collectBulkOneDriveDeleteIds(
  documents,
  draftByKey,
  settingsDraft,
  bulk,
  docInfos,
) {
  const ids = new Set();
  const results = Array.isArray(bulk?.results) ? bulk.results : [];
  const submitted = Array.isArray(docInfos) ? docInfos : [];

  for (let i = 0; i < results.length; i++) {
    if (results[i]?.statusCode !== 200) {
      continue;
    }

    const doc = documents[i];
    // Local Browse files are not OneDrive items — skip Graph delete.
    if (doc?.source === "browse") {
      continue;
    }

    const draft = doc?.key != null ? draftByKey[String(doc.key)] : null;
    if (!resolveOneDriveDeleteRequired(draft, settingsDraft)) {
      continue;
    }

    const oneDriveId =
      submitted[i]?.oneDriveDocumentId ??
      results[i]?.oneDriveDocumentId ??
      (doc?.key != null ? String(doc.key) : null);
    if (oneDriveId) {
      ids.add(String(oneDriveId));
    }
  }

  return [...ids];
}

/**
 * Build OneDrive metadata write-backs for a completed bulk check-in.
 *
 * One entry per successful item whose OneDrive original is kept, carrying the
 * IFS-assigned document identity (DocNo etc.) so the OneDrive Documents list can
 * show the document number afterwards. Deleted originals are skipped - there is
 * nothing left to update. Results are aligned by index with the submitted
 * `docInfos` / `documents` arrays, matching collectBulkOneDriveDeleteIds.
 */
export function collectBulkMetadataUpdates(
  documents,
  draftByKey,
  settingsDraft,
  bulk,
  docInfos,
) {
  const updates = [];
  const results = Array.isArray(bulk?.results) ? bulk.results : [];
  const submitted = Array.isArray(docInfos) ? docInfos : [];

  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    if (result?.statusCode !== 200) {
      continue;
    }

    const doc = documents[i];
    const draft = doc?.key != null ? draftByKey[String(doc.key)] : null;

    // Local Browse files are not OneDrive items — skip Graph metadata updates.
    if (doc?.source === "browse") {
      continue;
    }

    // The original is about to be removed from OneDrive - no metadata to write.
    if (resolveOneDriveDeleteRequired(draft, settingsDraft)) {
      continue;
    }

    const documentId =
      submitted[i]?.oneDriveDocumentId ??
      result?.oneDriveDocumentId ??
      (doc?.key != null ? String(doc.key) : null);
    if (!documentId) {
      continue;
    }

    const releaseStatus = draft?.releaseOverridden
      ? draft?.docReleaseStatus ?? ""
      : settingsDraft?.docReleaseStatus ?? "";

    updates.push({
      documentId: String(documentId),
      docClass: result.docClass ?? "",
      docNo: result.docNo ?? "",
      docRev: result.docRev ?? "",
      docSheet: result.docSheet ?? "1",
      docFormat: result.docFormat ?? "",
      state: releaseStatus || "",
      // Preserve download flag so leftover Working Document icons stay correct.
      isDownloaded: isWorkingDocumentDownloaded(doc),
    });
  }

  return updates;
}

/** Match a bulk API result row back to the OneDrive list document (by index first). */
export function findDocForBulkResult(documents, draftByKey, result, resultIndex) {
  if (!result) {
    return null;
  }

  if (
    typeof resultIndex === "number" &&
    resultIndex >= 0 &&
    resultIndex < documents.length
  ) {
    return documents[resultIndex] ?? null;
  }

  const resultFileName =
    typeof result.fileName === "string" ? result.fileName.trim() : "";

  if (resultFileName) {
    const byFileName = documents.find((doc) => {
      const fileName = doc?.name ?? doc?.title ?? "";
      const sanitized = fileName ? sanitizeFileName(fileName) : "";
      return sanitized && sanitized === resultFileName;
    });
    if (byFileName) {
      return byFileName;
    }
  }

  const resultTitle = typeof result.title === "string" ? result.title.trim() : "";
  if (!resultTitle) {
    return null;
  }

  return (
    documents.find((doc) => {
      const draft = draftByKey[String(doc.key)];
      return draft?.docTitle === resultTitle;
    }) ?? null
  );
}

/** Reset shared check-in Redux state (same as CheckInNew.clearCheckin). */
export function clearBulkCheckinState({
  onDocTitleChange,
  onDocClassFormatChange,
  onFileUploadChange,
  onViewCopyUploadChange,
  onDocReleaseChange,
  onCreateViewCopyDisable,
  onCreateViewCopySelect,
  onObjectConnectionDataLoad,
  onDocClassFormatListChange,
  onRecentObjectConnectionChanged,
  onSelectedObjectConnectionChanged,
  onSelectedKeyLuNameChange,
  onRecentObjectSelectedDescription,
  onRecentObjectDataLoadStatusChanged,
  docClassFormatList,
  selectedObjectConnections,
}) {
  onDocTitleChange("");
  onDocClassFormatChange("", "", "N", "N", "N", null, 0);
  onFileUploadChange(null, 0, 0, false);
  onViewCopyUploadChange(null, 0, 0, false, null);
  onDocReleaseChange(false);
  onCreateViewCopyDisable(true);
  onCreateViewCopySelect(false, { programmatic: true });
  onObjectConnectionDataLoad(DATA_LOAD_STATUS.onStart);
  cleanAll();
  if (Array.isArray(docClassFormatList)) {
    onDocClassFormatListChange(resetHierarchy(docClassFormatList));
  }
  onRecentObjectConnectionChanged([]);
  onRecentObjectDataLoadStatusChanged(DOCUMENT_LOAD_STATUS.onStart);
  if (
    Array.isArray(selectedObjectConnections) &&
    selectedObjectConnections.length > 0
  ) {
    onSelectedObjectConnectionChanged([], []);
  }
  onSelectedKeyLuNameChange("");
  onRecentObjectSelectedDescription("");
}

/**
 * Parse an IFS document name for a bulk browse file, reusing the existing
 * validation. Returns null when the file is not a browse file or its name is
 * not a valid IFS document name.
 * @returns {{fileName: string, parsed: object}|null}
 */
export function getBulkBrowseIFSInfo(doc) {
  if (doc?.source !== "browse") {
    return null;
  }
  const fileName = doc?.name ?? doc?.title ?? "";
  if (typeof fileName !== "string" || fileName.trim() === "") {
    return null;
  }
  const parsed = parseIFSFileName(fileName);
  if (
    !parsed ||
    !parsed.isIFSFormat ||
    !parsed.docClass ||
    !parsed.docNo ||
    !parsed.docRev
  ) {
    return null;
  }
  return { fileName: fileName.trim(), parsed };
}

/**
 * Resolve IFS identifiers for a bulk check-in document before auto-populating
 * class, format and object connections. Supports two sources:
 *  - Browse files (local): IFS identity is parsed from the file name.
 *  - Channel/OneDrive documents that are already checked in: the list/API call
 *    carries the IFS metadata (document number, class, rev, sheet, format size),
 *    so the identity is read straight from the document metadata.
 * Returns null when the document carries no IFS identity (e.g. a brand-new file
 * that has never been checked in), so the manual flow / channel-config defaults
 * are kept untouched.
 * @returns {{fileName: string, parsed: object}|null}
 */
export function resolveBulkIFSIdentifiers(doc) {
  const fileName = doc?.name ?? doc?.title ?? "";
  if (typeof fileName !== "string" || fileName.trim() === "") {
    return null;
  }
  if (doc?.source === "browse") {
    return getBulkBrowseIFSInfo(doc);
  }
  const docClass = doc?.docClass ?? doc?.DocClass ?? "";
  const docNo = doc?.docNo ?? doc?.DocNo ?? "";
  const docRev = doc?.docRev ?? doc?.DocRev ?? "";
  if (!docClass || !docNo || !docRev) {
    return null;
  }
  return {
    fileName: fileName.trim(),
    parsed: {
      docClass,
      docNo,
      docSheet: doc?.docSheet ?? doc?.DocSheet ?? "1",
      docRev,
      formatSize: doc?.formatSize ?? doc?.FormatSize ?? "",
      isCheckedIn: true,
    },
  };
}

/** Normalize object connections from a document revision payload. */
export function extractBulkObjectConnections(revision) {
  const raw = revision?.ObjectConnections ?? revision?.objectConnections ?? [];
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }
  return raw.map((connection) => ({
    ...connection,
    objectID: connection?.objectID ?? connection?.ObjectID ?? "",
    objectType: connection?.objectType ?? connection?.ObjectType ?? "",
    objectNo:
      connection?.objectNo ??
      connection?.ObjectNo ??
      connection?.objectNumber ??
      "",
    objectName: connection?.objectName ?? connection?.ObjectName ?? "",
  }));
}

/** Parse class rules/formats from a formats-by-class payload. */
export function parseBulkClassRules(payload) {
  if (!payload) {
    return {
      formats: [],
      viewRequired: "N",
      formatRequired: "N",
      objConRequired: "N",
      restrictedAccess: "N",
    };
  }
  const classItems = Array.isArray(payload)
    ? payload
    : payload?.classDetails ??
      payload?.ClassDetails ??
      payload?.value?.classDetails ??
      payload?.value?.ClassDetails ??
      [];
  if (!Array.isArray(classItems) || classItems.length === 0) {
    return {
      formats: [],
      viewRequired: "N",
      formatRequired: "N",
      objConRequired: "N",
      restrictedAccess: "N",
    };
  }
  const classItem = classItems[0];
  const formats = classItem?.formats ?? classItem?.Formats ?? [];
  return {
    formats: Array.isArray(formats) ? formats : [],
    viewRequired: `${classItem?.viewRequired ?? classItem?.ViewRequired ?? "N"}`,
    formatRequired: `${classItem?.formatMandatory ?? classItem?.FormatMandatory ?? "N"}`,
    objConRequired: `${classItem?.objectConnectionRequired ?? classItem?.ObjectConnectionRequired ?? "N"}`,
    restrictedAccess: `${classItem?.restrictedAccess ?? classItem?.RestrictedAccess ?? "N"}`,
  };
}

/** True when a format item matches the document format size (tolerant to case/whitespace). */
export function bulkFormatMatches(formatItem, formatSize) {
  if (!formatSize) {
    return false;
  }
  const candidate = formatItem?.formatSize ?? formatItem?.FormatSize;
  if (candidate === null || candidate === undefined) {
    return false;
  }
  return (
    String(candidate).trim().toLowerCase() ===
    String(formatSize).trim().toLowerCase()
  );
}

/**
 * Merge autopopulated IFS document details into a bulk file draft. Existing
 * overrides and attachments are preserved; only the fields resolved from the
 * document are applied. `createViewCopy` (auto-generate) is set true when the
 * class requires a view copy and the original file is a Word document, matching
 * the single check-in flow.
 */
export function mergeBulkAutopopulatedDraft(
  draft,
  {
    docClass,
    docFormat,
    viewRequired,
    formatRequired,
    objConRequired,
    objectConnections,
    autoCreateViewCopy = false,
  },
) {
  const hasConnections = Array.isArray(objectConnections) && objectConnections.length > 0;
  return {
    ...draft,
    docClass: docClass ?? "",
    docFormat: docFormat ?? "",
    overridden: true,
    viewRequired: viewRequired ?? "N",
    formatRequired: formatRequired ?? "N",
    objConRequired: objConRequired ?? "N",
    createViewCopy:
      autoCreateViewCopy === true ? true : (draft?.createViewCopy ?? false),
    viewCopyOverridden:
      autoCreateViewCopy === true ? true : (draft?.viewCopyOverridden ?? false),
    objectConnections: hasConnections ? objectConnections : (draft?.objectConnections ?? []),
    objectConnectionsOverridden: hasConnections,
  };
}
